// pages/entry-form/RoiInfoPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip, Dialog,
  DialogContent, DialogActions, CircularProgress, Alert, Tooltip, alpha,
  Fade, Card, Stack, MenuItem, Select, FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, TrendingUp as RoiIcon, Close as CloseIcon,
  CalendarToday as CalendarIcon, Person as PersonIcon,
  AttachMoney as MoneyIcon, SwapVert as TypeIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { tradesService, RoiInfo, RoiInfoPayload } from '../../services/tradesService';

// ── Accent colour ─────────────────────────────────────────────────────────────
const ACCENT      = '#2E7D32';   // deep green — "investment" feel
const INVEST_CLR  = '#2E7D32';
const RETURN_CLR  = '#1565C0';

// ── Permission helper ─────────────────────────────────────────────────────────
const usePermissions = () => {
  const { user } = useAuth();
  const p = user?.role?.permissions?.[0];
  return {
    canCreate: p?.create_permission ?? false,
    canUpdate: p?.update_permission ?? false,
    canDelete: p?.delete_permission || user?.role?.role_name === 'DATA_MANAGER' || user?.is_superuser,
  };
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v: string | number) =>
  Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const today = () => new Date().toISOString().split('T')[0];

const formatDateForInput = (dateStr: string | null | undefined): string => {
  if (!dateStr) return today();
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return today();
};

const formatDateDisplay = (dateStr: string): string => {
  const d     = new Date(dateStr + 'T00:00:00');
  const day   = String(d.getDate()).padStart(2, '0');
  const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  return `${day}-${month}-${d.getFullYear()}`;
};

const ROI_TYPES: Array<'Invest' | 'Return'> = ['Invest', 'Return'];

// ══════════════════════════════════════════════════════════════════════════════
// Form Dialog
// ══════════════════════════════════════════════════════════════════════════════
interface FV {
  invest_return_date:   string;
  investor_name:        string;
  roi_type_name:        'Invest' | 'Return';
  invest_return_amount: string;
  remarks:              string;
}

const RoiFormDialog: React.FC<{
  open:    boolean;
  editing: RoiInfo | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, editing, onClose, onSaved }) => {
  const [saving, setSaving]     = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    control, handleSubmit, reset, formState: { errors },
  } = useForm<FV>({
    defaultValues: {
      invest_return_date:   today(),
      investor_name:        '',
      roi_type_name:        'Invest',
      invest_return_amount: '',
      remarks:              '',
    },
  });

  useEffect(() => {
    if (!open) return;
    setApiError('');
    if (editing) {
      reset({
        invest_return_date:   formatDateForInput(editing.invest_return_date),
        investor_name:        editing.investor_name,
        roi_type_name:        editing.roi_type_name,
        invest_return_amount: editing.invest_return_amount,
        remarks:              editing.remarks || '',
      });
    } else {
      reset({
        invest_return_date:   today(),
        investor_name:        '',
        roi_type_name:        'Invest',
        invest_return_amount: '',
        remarks:              '',
      });
    }
  }, [open, editing, reset]);

  const onSubmit = async (data: FV) => {
    setSaving(true); setApiError('');
    try {
      const payload: RoiInfoPayload = {
        invest_return_date:   data.invest_return_date,
        investor_name:        data.investor_name.trim(),
        roi_type_name:        data.roi_type_name,
        invest_return_amount: parseFloat(data.invest_return_amount || '0'),
        remarks:              data.remarks.trim() || undefined,
      };
      if (editing) await tradesService.updateRoi(editing.roi_info_id, payload);
      else         await tradesService.createRoi(payload);
      onSaved(); onClose();
    } catch (e: any) {
      const errs = e?.data?.errors;
      setApiError(
        errs?.investor_name?.[0] || errs?.invest_return_amount?.[0] ||
        errs?.roi_type_name?.[0] || e?.data?.message || 'Something went wrong.'
      );
    } finally { setSaving(false); }
  };

  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      {/* Gradient header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, #1B5E20 100%)`,
        px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RoiIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            {editing ? 'Edit ROI Entry' : 'New ROI Entry'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{apiError}</Alert>
          )}
          <Stack spacing={2.5}>

            {/* Date */}
            <Controller name="invest_return_date" control={control}
              rules={{
                required: 'Date is required',
                max: { value: today(), message: 'Future date not allowed' },
              }}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Invest / Return Date *" type="date"
                  error={!!errors.invest_return_date}
                  helperText={errors.invest_return_date?.message}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today() }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" sx={{ color: ACCENT }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              )}
            />

            {/* Investor Name */}
            <Controller name="investor_name" control={control}
              rules={{ required: 'Investor name is required' }}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Investor Name *"
                  error={!!errors.investor_name}
                  helperText={errors.investor_name?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" sx={{ color: ACCENT }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              )}
            />

            {/* ROI Type */}
            <Controller name="roi_type_name" control={control}
              rules={{ required: 'ROI type is required' }}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.roi_type_name} sx={fieldSx}>
                  <InputLabel>ROI Type *</InputLabel>
                  <Select
                    {...field} label="ROI Type *"
                    startAdornment={
                      <InputAdornment position="start">
                        <TypeIcon fontSize="small" sx={{ color: ACCENT }} />
                      </InputAdornment>
                    }
                  >
                    {ROI_TYPES.map(t => (
                      <MenuItem key={t} value={t}>
                        <Chip
                          size="small" label={t}
                          sx={{
                            background: alpha(t === 'Invest' ? INVEST_CLR : RETURN_CLR, 0.1),
                            color:      t === 'Invest' ? INVEST_CLR : RETURN_CLR,
                            fontWeight: 700, height: 22, fontSize: '0.75rem',
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.roi_type_name && (
                    <FormHelperText>{errors.roi_type_name.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {/* Amount */}
            <Controller name="invest_return_amount" control={control}
              rules={{
                required: 'Amount is required',
                min:      { value: 0, message: 'Amount cannot be negative' },
                validate: v => !isNaN(parseFloat(v)) || 'Enter a valid number',
              }}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Amount (Tk.) *" type="number"
                  inputProps={{ step: '0.01', min: '0' }}
                  error={!!errors.invest_return_amount}
                  helperText={errors.invest_return_amount?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon fontSize="small" sx={{ color: ACCENT }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              )}
            />

            {/* Remarks */}
            <Controller name="remarks" control={control}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Remarks" multiline rows={2}
                  sx={fieldSx}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" disabled={saving}
            sx={{ borderRadius: 6, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}
            sx={{ borderRadius: 6, background: `linear-gradient(135deg, ${ACCENT}, #1B5E20)`, px: 3 }}>
            {saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : (editing ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Delete Confirm Dialog
// ══════════════════════════════════════════════════════════════════════════════
const DeleteDialog: React.FC<{
  open:      boolean;
  id:        number | null;
  onClose:   () => void;
  onDeleted: () => void;
}> = ({ open, id, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => { if (open) setError(''); }, [open]);

  const handleDelete = async () => {
    if (!id) return;
    setLoading(true);
    try {
      await tradesService.deleteRoi(id);
      onDeleted(); onClose();
    } catch (e: any) {
      setError(e?.data?.message || 'Failed to delete entry.');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogContent sx={{ pt: 3, pb: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography fontWeight={700} gutterBottom>Delete ROI Entry</Typography>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete this ROI entry? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ borderRadius: 6 }}>
          Cancel
        </Button>
        <Button onClick={handleDelete} disabled={loading} variant="contained" color="error"
          sx={{ borderRadius: 6 }}>
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
const RoiInfoPage: React.FC = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();

  const [rows,     setRows]     = useState<RoiInfo[]>([]);
  const [totals,   setTotals]   = useState<{ total_invest: number; total_return: number; total_amount: number }>({
    total_invest: 0, total_return: 0, total_amount: 0,
  });
  const [loading,  setLoading]  = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing,  setEditing]  = useState<RoiInfo | null>(null);
  const [delId,    setDelId]    = useState<number | null>(null);

  // Filters
  const [search,   setSearch]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [filterType, setFilterType] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: Record<string, string> = {};
      if (search)     p.search    = search;
      if (dateFrom)   p.date_from = dateFrom;
      if (dateTo)     p.date_to   = dateTo;
      if (filterType) p.roi_type  = filterType;
      const res = await tradesService.listRois(p);
      setRows(res.results);
      setTotals(res.totals as any ?? { total_invest: 0, total_return: 0, total_amount: 0 });
    } catch {
      // silently fail
    } finally { setLoading(false); }
  }, [search, dateFrom, dateTo, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearFilters = () => {
    setSearch(''); setDateFrom(''); setDateTo(''); setFilterType('');
  };

  const typeColor = (t: string) => t === 'Invest' ? INVEST_CLR : RETURN_CLR;

  const formatDate = (date: string | Date) => {
    let d: Date;
    
    if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}/)) {
      // Parse DD-MM-YYYY HH:mm:ss format
      const [datePart, timePart] = date.split(' ');
      const [day, month, year] = datePart.split('-');
      const timeString = timePart || '00:00:00';
      // Create date in YYYY-MM-DD format for proper parsing
      d = new Date(`${year}-${month}-${day}T${timeString}`);
    } else {
      d = new Date(date);
    }
    
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    
    return `${day}-${month}-${year}`;
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT}, #1B5E20)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <RoiIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                ROI Information
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Track investor contributions and returns
              </Typography>
            </Box>
          </Box>
          {canCreate && (
            <Button
              variant="contained" startIcon={<AddIcon />}
              onClick={() => { setEditing(null); setFormOpen(true); }}
              sx={{
                borderRadius: 6, px: 2.5, fontWeight: 700,
                background: `linear-gradient(135deg, ${ACCENT}, #1B5E20)`,
                boxShadow: `0 4px 14px ${alpha(ACCENT, 0.35)}`,
              }}
            >
              Add ROI Entry
            </Button>
          )}
        </Box>

        {/* ── Summary Cards ────────────────────────────────────────────────── */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Invest (Tk.)', value: totals.total_invest, color: INVEST_CLR },
            { label: 'Total Return (Tk.)', value: totals.total_return, color: RETURN_CLR },
            // { label: 'Net Balance (Tk.)',  value: totals.total_return - totals.total_invest, color: '#FF9800' },
            { label: 'Records',          value: rows.length, color: '#6A1B9A', isCount: true },
          ].map(c => (
            <Card key={c.label} elevation={0} sx={{
              flex: '1 1 140px', borderRadius: 3,
              border: `1px solid ${alpha(c.color, 0.2)}`,
              background: alpha(c.color, 0.05), p: 2,
            }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{c.label}</Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: c.color }}>
                {c.isCount ? rows.length : fmt(c.value)}
              </Typography>
            </Card>
          ))}
        </Stack>

        {/* ── Filter Bar ──────────────────────────────────────────────────── */}
        <Card elevation={0} sx={{
          borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.12)}`, p: 2, mb: 2,
        }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
            <TextField size="small" placeholder="Search investor…" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}
            />

            <TextField size="small" label="From" type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}
            />
            <TextField size="small" label="To" type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}
            />

            <FormControl size="small" sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}>
              <InputLabel>ROI Type</InputLabel>
              <Select value={filterType} label="ROI Type"
                onChange={e => setFilterType(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {ROI_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchData}
                sx={{ color: ACCENT, border: `1px solid ${alpha(ACCENT, 0.3)}`, borderRadius: 2 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button size="small" variant="outlined" onClick={clearFilters}
              sx={{
                borderRadius: 6, borderColor: alpha(ACCENT, 0.5), color: ACCENT,
                fontWeight: 600, px: 2,
                '&:hover': { borderColor: ACCENT, background: alpha(ACCENT, 0.06) },
              }}>
              Clear
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {rows.length} record{rows.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </Card>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <Card elevation={0} sx={{
          borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}`, overflow: 'hidden',
        }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: alpha(ACCENT, 0.04) }}>
                  {['#', 'Date', 'Investor Name', 'ROI Type', 'Amount (Tk.)', 'Remarks', 'Created By', 'Actions'].map(h => (
                    <TableCell key={h} sx={{
                      fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary',
                      py: 1.5, borderBottom: `2px solid ${alpha(ACCENT, 0.12)}`, whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={32} sx={{ color: ACCENT }} />
                    </TableCell>
                  </TableRow>
                )}

                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <RoiIcon sx={{ fontSize: 40, color: 'text.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No ROI entries found</Typography>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && rows.map((r, idx) => (
                  <TableRow key={r.roi_info_id}
                    sx={{ '&:hover': { background: alpha(ACCENT, 0.03) }, transition: 'background 0.15s' }}>

                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.8rem' }}>
                      {idx + 1}
                    </TableCell>

                    <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {formatDate(r.invest_return_date)}
                    </TableCell>

                    <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.875rem', maxWidth: 200 }}>
                      <Typography noWrap fontWeight={600} fontSize="inherit" title={r.investor_name}>
                        {r.investor_name}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.25 }}>
                      <Chip
                        size="small" label={r.roi_type_name}
                        sx={{
                          background: alpha(typeColor(r.roi_type_name), 0.1),
                          color:      typeColor(r.roi_type_name),
                          fontWeight: 700, height: 22, fontSize: '0.72rem',
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{
                      py: 1.25, textAlign: 'right', fontWeight: 700,
                      color: typeColor(r.roi_type_name),
                    }}>
                      {fmt(r.invest_return_amount)}
                    </TableCell>

                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.78rem', maxWidth: 150 }}>
                      <Typography noWrap variant="body2" fontSize="inherit">
                        {r.remarks || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.78rem' }}>
                      {r.created_by}
                    </TableCell>

                    <TableCell sx={{ py: 1.25 }}>
                      <Stack direction="row" spacing={0.5}>
                        {canUpdate && (
                          <Tooltip title="Edit">
                            <IconButton size="small"
                              onClick={() => { setEditing(r); setFormOpen(true); }}
                              sx={{ color: ACCENT, '&:hover': { background: alpha(ACCENT, 0.1) } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton size="small"
                              onClick={() => setDelId(r.roi_info_id)}
                              sx={{ color: '#f44336', '&:hover': { background: alpha('#f44336', 0.1) } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Totals footer */}
                {rows.length > 0 && !loading && (
                  <TableRow sx={{ background: alpha(ACCENT, 0.06) }}>
                    <TableCell colSpan={3} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>
                      TOTALS
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" label={`Invest: Tk. ${fmt(totals.total_invest)}`}
                          sx={{ background: alpha(INVEST_CLR, 0.1), color: INVEST_CLR, fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                        <Chip size="small" label={`Return: Tk. ${fmt(totals.total_return)}`}
                          sx={{ background: alpha(RETURN_CLR, 0.1), color: RETURN_CLR, fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800' }}>
                      {fmt(totals.total_amount)}
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Dialogs */}
        <RoiFormDialog
          open={formOpen} editing={editing}
          onClose={() => setFormOpen(false)}
          onSaved={fetchData}
        />
        <DeleteDialog
          open={!!delId} id={delId}
          onClose={() => setDelId(null)}
          onDeleted={fetchData}
        />
      </Box>
    </Fade>
  );
};

export default RoiInfoPage;
